import React, { useEffect, useRef, useState } from "react";
import { Reorder } from "framer-motion";
import { Plus, Save, RotateCcw, Loader2, FileText, ArrowLeft, Lock } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { BlockCard } from "../../components/templates/BlockCard";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../../components/ui/select";
import { LogoCard } from "../../components/templates/LogoCard";
import { EmailCard } from "../../components/templates/EmailCard";

const FIXED = ["header", "amounts", "signature", "footer", "lease_terms", "clauses"];

function Preview({ kind, blocks, accent, v }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    if (!blocks) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.post(`/doc-templates/${kind}/preview`, { blocks, accent }, { responseType: "blob" });
        setUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(r.data); });
      } catch { /* aperçu indisponible */ } finally { setBusy(false); }
    }, 700);
    return () => clearTimeout(timer.current);
  }, [kind, blocks, accent, v]);
  return (
    <div className="relative bg-brand-softer rounded-2xl border border-brand-soft h-[78vh] overflow-hidden" data-testid="tpl-preview">
      {busy && <span className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 text-xs bg-white/90 px-2 py-1 rounded-full"><Loader2 className="w-3 h-3 animate-spin" /> Mise à jour…</span>}
      {url ? <iframe title="Aperçu" src={`${url}#toolbar=0&view=FitH`} className="w-full h-full" data-testid="tpl-preview-frame" /> : <div className="h-full grid place-items-center text-sm text-brand-green/50">Chargement de l'aperçu…</div>}
    </div>
  );
}

export default function ModelesDocuments() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [types, setTypes] = useState([]);
  const [hasLogo, setHasLogo] = useState(false);
  const [logoV, setLogoV] = useState(0);
  const [kind, setKind] = useState(params.get("kind") || "quittance");
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { api.get("/doc-templates").then((r) => { setTypes(r.data.types); setHasLogo(r.data.has_logo); }); }, []);
  useEffect(() => { setTpl(null); setDirty(false); api.get(`/doc-templates/${kind}`).then((r) => setTpl(r.data)).catch(() => setKind("quittance")); }, [kind]);

  const update = (patch) => { setTpl((t) => ({ ...t, ...patch })); setDirty(true); };
  const setBlocks = (blocks) => update({ blocks });
  const addText = () => {
    const i = tpl.blocks.findIndex((b) => b.type === "signature");
    const at = i >= 0 ? i : tpl.blocks.length - 1;
    setBlocks([...tpl.blocks.slice(0, at), { id: `txt_${Date.now()}`, type: "text", enabled: true, text: "Nouveau texte — {locataire}", align: "left", size: 10, bold: false, muted: false, locked: false }, ...tpl.blocks.slice(at)]);
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/doc-templates/${kind}`, { blocks: tpl.blocks, accent: tpl.accent, email: tpl.email || null });
      setTpl((t) => ({ ...t, blocks: r.data.blocks, customized: true }));
      toast.success("Modèle enregistré : il sera utilisé pour tous les documents générés");
      setDirty(false); setTypes((ts) => ts.map((t) => (t.key === kind ? { ...t, customized: true } : t)));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur à l'enregistrement"); } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Revenir au modèle par défaut ? Vos personnalisations seront perdues.")) return;
    const r = await api.delete(`/doc-templates/${kind}`);
    setTpl((t) => ({ ...t, ...r.data, customized: false })); setDirty(false);
    setTypes((ts) => ts.map((t) => (t.key === kind ? { ...t, customized: false } : t)));
    toast.success("Modèle par défaut restauré");
  };

  return (
    <div className="space-y-5" data-testid="modeles-documents-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <button onClick={() => nav("/app/quittances")} className="inline-flex items-center gap-1 text-xs text-brand-green/60 hover:text-brand-greenDeep mb-1" data-testid="tpl-back-btn"><ArrowLeft className="w-3.5 h-3.5" /> Quittances</button>
          <h1 className="font-display text-3xl text-brand-greenDeep">Modèles de documents</h1>
          <p className="text-sm text-brand-green/60">Réorganisez les blocs, modifiez les textes et insérez des variables : le modèle s'applique à tous les documents générés.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} disabled={!tpl?.customized && !dirty} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-soft text-sm text-brand-greenDeep hover:bg-brand-softer disabled:opacity-40" data-testid="tpl-reset-btn"><RotateCcw className="w-4 h-4" /> Par défaut</button>
          <button onClick={save} disabled={saving || !tpl || !dirty} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-50" data-testid="tpl-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer{dirty ? " *" : ""}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3" data-testid="tpl-kind-tabs">
        <FileText className="w-5 h-5 text-brand-limeDark" />
        <Select value={kind} onValueChange={(v) => (!dirty || window.confirm("Modifications non enregistrées. Continuer ?")) && setKind(v)}>
          <SelectTrigger className="w-full sm:w-[420px] rounded-full bg-white border-brand-soft" data-testid="tpl-kind-select"><SelectValue placeholder="Choisir un document" /></SelectTrigger>
          <SelectContent className="max-h-[60vh]">
            {[...new Set(types.map((t) => t.group))].map((g) => (
              <SelectGroup key={g}>
                <SelectLabel className="text-[11px] uppercase tracking-wider text-brand-green/50">{g}</SelectLabel>
                {types.filter((t) => t.group === g).map((t) => (
                  <SelectItem key={t.key} value={t.key} data-testid={`tpl-kind-${t.key}`}>{t.label}{t.customized ? " · perso" : ""}</SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-brand-green/50" data-testid="tpl-kind-count">{types.length} modèles · {types.filter((t) => t.customized).length} personnalisé(s)</span>
      </div>
      {!tpl ? <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-brand-limeDark" /></div> : (
        <div className="grid lg:grid-cols-2 gap-5 items-start">
          <div className="space-y-3">
            <LogoCard hasLogo={hasLogo} onChange={(v) => { setHasLogo(v); setLogoV((x) => x + 1); }} />
            {tpl.blocks.some((b) => b.locked) && (
              <p className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-3 py-2" data-testid="tpl-locked-info">
                <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {{ Baux: "Les blocs « Légal » reprennent les clauses imposées par le contrat type (décret n° 2015-587) ou la loi.", Quittances: "Les blocs « Légal » reprennent les mentions obligatoires de la quittance ou de la facture.", Courriers: "Les blocs « Légal » reprennent les mentions et calculs qui donnent sa valeur au courrier (indices, montants dus, mise en demeure)." }[types.find((t) => t.key === kind)?.group] || "Les blocs « Légal » reprennent des mentions obligatoires."} Vous pouvez les déplacer et changer leur taille, mais pas les modifier ni les supprimer. Tout le reste est libre.
              </p>
            )}
            <div className="flex flex-wrap items-center gap-3 text-xs text-brand-green/70">
              <label className="flex items-center gap-2">Couleur d'accent <input type="color" value={tpl.accent || "#B8925A"} onChange={(e) => update({ accent: e.target.value })} className="w-8 h-8 rounded border border-brand-soft" data-testid="tpl-accent" /></label>
              <span className="text-brand-green/50">Astuce : **texte** = gras · glissez la poignée pour déplacer un bloc</span>
            </div>
            <Reorder.Group axis="y" values={tpl.blocks} onReorder={setBlocks} className="space-y-2" data-testid="tpl-blocks">
              {tpl.blocks.map((b, i) => (
                <BlockCard key={b.id} block={b} variables={tpl.variables} onChange={(nb) => setBlocks(tpl.blocks.map((x, j) => (j === i ? nb : x)))}
                  onRemove={FIXED.includes(b.type) || b.locked ? null : () => setBlocks(tpl.blocks.filter((_, j) => j !== i))} />
              ))}
            </Reorder.Group>
            <button onClick={addText} className="inline-flex items-center gap-2 text-sm font-medium text-brand-greenDeep hover:text-brand-limeDark" data-testid="tpl-add-text"><Plus className="w-4 h-4" /> Ajouter un bloc de texte</button>
            {tpl.email && <EmailCard email={tpl.email} variables={tpl.variables} onChange={(email) => update({ email })} />}
          </div>
          <div className="lg:sticky lg:top-4"><Preview kind={kind} blocks={tpl.blocks} accent={tpl.accent} v={logoV} /></div>
        </div>
      )}
    </div>
  );
}
